// Empty-state screen shown after onboarding completes but before any
// project exists (or after the user deletes their last project).
//
// Single CTA: type a name + click "建立"; the projects store handles slug
// normalization, OPFS dir creation, and active-project bookkeeping.
//
// We deliberately don't try to be clever here — no template gallery, no
// recent-files dropdown, no Drive/Dropbox import. Phase 2's job is to make
// the studio USABLE on first run; richer project management arrives once
// real users hit pain points.

import { useState } from 'react';

import { useProjects } from '../state/projects';
import { slugifyProjectName } from '../lib/slug';

export function NoProjectScreen() {
  const projects = useProjects((s) => s.projects);
  const createProject = useProjects((s) => s.createProject);
  const setActive = useProjects((s) => s.setActive);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = name.trim();
  const previewSlug = slugifyProjectName(trimmed);
  const canSubmit = previewSlug.length > 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    setBusy(true);
    try {
      await createProject(trimmed);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-[#0d0d12] px-6 py-12 text-neutral-300">
      <div className="w-full max-w-md">
        <h2 className="miniken-wordmark text-[24px] leading-tight">
          MiniKen<span className="period">.</span>
        </h2>
        <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-neutral-600">
          Design Studio · Open
        </p>
        <h3 className="mt-8 text-[16px] font-medium text-neutral-200">
          開始第一個 project
        </h3>
        <p className="mt-2 text-[12px] leading-relaxed text-neutral-500">
          每個 project 係瀏覽器入面一個獨立 folder。可以隨時改名、刪除、export PDF。
        </p>

        <label className="mt-6 block text-[11px] uppercase tracking-[0.12em] text-neutral-500">
          Project 名稱
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit();
          }}
          autoFocus
          placeholder="例：Genesis Saver pitch deck"
          className="mt-2 w-full rounded-md border border-[#222230] bg-[#141419] px-3 py-2 text-[13px] text-neutral-100 placeholder:text-neutral-600 focus:border-[#8AFF45] focus:outline-none"
        />
        {trimmed && previewSlug !== trimmed && (
          <p className="mt-2 text-[10px] text-neutral-600">
            Folder 名會係 <span className="text-neutral-400">{previewSlug}</span>
          </p>
        )}
        {error && (
          <p className="mt-2 text-[11px] text-[#ff8a8a]">{error}</p>
        )}

        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => void submit()}
          className="mt-4 w-full rounded-md bg-[#8AFF45] px-4 py-2 text-[13px] font-medium text-[#0d0d12] disabled:cursor-not-allowed disabled:bg-[#222230] disabled:text-neutral-600"
        >
          {busy ? '建立中…' : '建立 project'}
        </button>

        {projects.length > 0 && (
          <div className="mt-10">
            <p className="text-[11px] uppercase tracking-[0.12em] text-neutral-600">
              最近 projects
            </p>
            <ul className="mt-2 space-y-1">
              {projects.slice(0, 5).map((p) => (
                <li key={p.name}>
                  <button
                    type="button"
                    onClick={() => void setActive(p.name)}
                    className="block w-full rounded-md border border-transparent px-2 py-1.5 text-left text-[12px] text-neutral-300 hover:border-[#222230] hover:bg-[#141419]"
                  >
                    {p.name}
                    <span className="ml-2 text-[10px] text-neutral-600">
                      {new Date(p.lastOpenedAt).toLocaleDateString()}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </main>
  );
}
